import { Tooltip } from '@mantine/core';
import { IconSparkles } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { stepIdToPrevOpOrderAtom } from '~/atoms/atoms';
import { fastAutoHealAtom } from '~/atoms/recorder-atoms';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import { api } from '~/trpc-extra/api';
import OperationControl from '../OperationControl';
import type { Part } from './converters';
import { operationToParts, partsToOperationData } from './converters';
import { OpRendererPart } from './OpRendererPart';
import { useUpdateStepDescription } from '~/hooks/mutations/steps/useUpdateStepDescription';
import { clsx } from 'clsx';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';

export default function OpRenderer({
  stepId,
  parentContexts,
  stepOrder,
  testContentId,
  operation,
  readonly,
  editing,
  disableSelect,
  setEditing,
  disableMenu,
  previewCursorAllowed,
  moveCursorEnabled,
  selected,
}: {
  stepId: string;
  // parentContext: see callback-handles.ts comment
  parentContexts?: OperationParentContext[];
  stepOrder: number;
  testContentId: string;
  operation: SelectedOperation;
  readonly?: boolean;
  editing: boolean;
  disableSelect?: boolean;
  setEditing: (isEditing: boolean) => void;
  disableMenu?: boolean;
  previewCursorAllowed: boolean;
  moveCursorEnabled: boolean;
  selected: boolean;
}) {
  const trpcUtils = api.useUtils();
  const parentContext = parentContexts?.at(-1);

  const [stepIdToPrevOpOrder, setStepIdToPrevOrder] = useAtom(stepIdToPrevOpOrderAtom);
  const [isSaving, setIsSaving] = useState(false);
  const { mutateAsync: upsertOperation } = api.operation.upsertOperation.useMutation();
  const fastAutoHealedOperations = useAtomValue(fastAutoHealAtom);
  const wasFastAutoHealed = fastAutoHealedOperations.some((item) => item.operationId === operation.id);
  // State to hold elements and cursor position
  const [parts, setParts] = useState<Part[]>(() =>
    operation ? operationToParts(operation) : [{ type: 'slot' } as const],
  ); // 0 indexed

  const secondTargetIndex = useMemo(() => {
    let count = 0;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (part.type === 'target') {
        count++;
      }
      if (count === 2) {
        return i;
      }
    }
    return -1;
  }, [parts]);

  useEffect(() => {
    setParts(operationToParts(operation));
  }, [setParts, operation]);

  // Function to update elements
  const updateParts = useCallback(
    (newParts: Part[]) => {
      setParts(newParts);
    },
    [setParts],
  );

  const { updateStepDescription } = useUpdateStepDescription({ testContentId });

  // Check if this operation contains AI Extraction
  const hasAIExtraction = useMemo(() => {
    return parts.some((part) => part.type === 'defineVariable' && part.valueType === 'AI_EXTRACT_PROMPT');
  }, [parts]);

  const handleSubmit = useCallback(
    async ({ parts, refreshStepDescription }: { parts: Part[]; refreshStepDescription: boolean }) => {
      if (isSaving) {
        return;
      }
      setIsSaving(true);

      const operationToSave = partsToOperationData({
        stepId,
        parentContext,
        operationId: operation.id,
        order:
          operation.order || (stepIdToPrevOpOrder?.stepId === stepId ? stepIdToPrevOpOrder.prevOpOrder + 1 : undefined),
        parts,
        pageAlias: operation.pageAlias,
      });

      try {
        // TODO: Optimistic update. Some issues here since the data of read/write operation is different
        // await trpcUtils.test.getTest.cancel({ id: testId });
        // // Get the data from the queryCache
        // const prevData = trpcUtils.test.getTest.getData();
        // trpcUtils.test.getTest.setData({ id: testId }, (old) => {
        //   if (!old) {
        //     return old;
        //   }
        //   return {
        //     ...old,
        //     steps: old.steps.map((step) =>
        //       step.id === stepId
        //         ? { ...step, operations: step.operations.map((op) => (op.id === operation?.id ? op : op)) }
        //         : step,
        //     ),
        //   };
        // });
        await upsertOperation({ operation: operationToSave });
        if (refreshStepDescription) {
          await updateStepDescription({ stepId });
        }
        // Don't want the step description update to block the operation from being saved
        await Promise.allSettled([
          trpcUtils.testContent.get.invalidate({ id: testContentId }),
          trpcUtils.testContent.getCode.invalidate({ testContentId }),
        ]);
        setStepIdToPrevOrder(undefined);
        setEditing(false);
      } catch (e) {
        console.error(e);
        notifications.show({ title: 'Error saving action', message: `${e}`, color: 'red' });
      } finally {
        setIsSaving(false);
      }
    },
    [
      isSaving,
      upsertOperation,
      operation,
      parts,
      stepId,
      stepIdToPrevOpOrder,
      setStepIdToPrevOrder,
      updateStepDescription,
    ],
  );

  return (
    <div
      className={clsx('group relative flex w-full items-center', {
        'hover:cursor-pointer': !disableSelect,
      })}
    >
      <Tooltip
        disabled={readonly || hasAIExtraction || !parts.some((p) => p.type === 'target')}
        openDelay={500}
        label={
          wasFastAutoHealed ?
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <IconSparkles size={12} />
                <span>AI found a new locator</span>
              </div>
              <div className="text-center text-xs">Double click to edit</div>
            </div>
          : 'Double click to edit'
        }
        withArrow
        arrowOffset={50}
        arrowSize={8}
        withinPortal={false}
      >
        <div className="flex min-w-0 flex-1 flex-wrap gap-1 px-1">
          {parts.map((part, index) => (
            <OpRendererPart
              key={index}
              testContentId={testContentId}
              operation={operation}
              index={index}
              secondTargetIndex={secondTargetIndex}
              part={part}
              parts={parts}
              updateParts={updateParts}
              handleSubmit={handleSubmit}
              readonly={readonly}
              setEditing={setEditing}
              parentContexts={parentContexts}
            />
          ))}
        </div>
      </Tooltip>
      {!disableMenu && (
        <div className={editing ? 'invisible' : ''}>
          <div
            className={clsx(
              'flex h-full items-center pr-1.5 opacity-0 transition-opacity delay-300 group-hover:opacity-100',
              selected && 'bg-gray-200 dark:bg-slate-700',
            )}
          >
            <OperationControl
              previewCursorAllowed={previewCursorAllowed}
              operation={operation}
              testContentId={testContentId}
              stepOrder={stepOrder}
              moveCursorEnabled={moveCursorEnabled}
              parentContext={parentContext}
              readonly={readonly}
            />
          </div>
        </div>
      )}
    </div>
  );
}
